import config from 'config';
import axios from 'axios';
import utils from './utils';

console.log('config.api', config);
const API_END_POINT = config.api.api;

class Client {
  constructor(opt = {}) {
    this.opt = Object.assign(this._getDefaultOption(), opt);
    this.request = axios.create(this.opt);
    this.isRefreshed = false;
    this.requestsToRefresh = [];
    this._interceptors();
  }

  _interceptors() {
    this.request.interceptors.response.use((response) => response,
      (error) => {
        const { response, config } = error;
        const status = response?.status;
        const message = response?.data?.message;
        const statusText = response?.statusText;
        const url = config?.url
        if (status === 401 && (statusText === 'Unauthorized' || message === 'Unauthenticated request') && !url.includes('/mvp/sign-in')) {
          if (!this.isRefreshed) {
            this.isRefreshed = true;

            this
              .refreshToken()
              .then((accessToken) => {
                this.requestsToRefresh.forEach((callback) => {
                  callback(accessToken);
                });
              })
              .catch((error) => {
                this.requestsToRefresh.forEach((callback) => callback(null));
              })
              .finally(() => {
                this.isRefreshed = false;
                this.requestsToRefresh = [];
              });
          }

          return new Promise((resolve, reject) => {
            this.requestsToRefresh.push((token) => {
              if (token) {
                let request = { ...config }
                delete request.headers.Authorization;
                delete request.headers.authorization;
                request.headers['Authorization'] = token;
                resolve(this.request(request));
              }

              reject(error);
            });
          });
        }
        return Promise.reject(error);
      },
    );
  }

  async refreshToken() {
    const refreshToken = await utils.getCookie('_RFT');
    if (!refreshToken) {
      return null;
    }

    const headers = { 'authorization': refreshToken };
    const data = await this.sendRequest({
      path: `/mvp/refresh-token`,
      method: 'POST',
      headers: headers,
    });

    if (data.status === 'OK') {
      const user = {
        type: data.user.type,
        name: data.user.name,
        _id: data.user._id,
        accessToken: data.accessToken,
      };

      localStorage.setItem('user-info', JSON.stringify(user));
      utils.saveToCookie('_RFT', data.refreshToken, 365);

      return data.accessToken;
    }
    return null;
  }

  // @private
  _getDefaultOption() {
    return {
      timeout: 60000,
    };
  }

  _initSocket(token) {
    if (this.sock) this.sock.close();
  }

  // @private
  _reset() {
    this.request = axios.create(this._getDefaultOption());
  }

  // @private
  _update(opt) {
    this.request = axios.create(Object.assign(this.opt, opt));
  }

  api(path, method = 'get', params = {}, headers) {
    const language = localStorage.getItem('language') || "vi"
    if (headers && headers.headers) {
      headers.headers['Accept-Language'] = language
    } else {
      headers = {
        headers: {
          'Accept-Language': language
        }
      }
    }
    return new Promise((resolve, reject) => {
      if (!path) {
        // path is required
        return reject(new Error('Used: api(path, method, params)'));
      }

      if (path.indexOf('http') !== 0) {
        // add '/' to path if missing
        if (path[0] !== '/') {
          path = '/' + path;
        }

        // use v1 version
        path = API_END_POINT + path;

        if (path.indexOf('?') !== -1) {
          path += '&_t=' + Date.now();
        } else {
          path += '?_t=' + Date.now();
        }
      }

      // support shorthand api(path, params)
      // method will default to get
      if (arguments.length === 2 && String(method) === '[object Object]') {
        params = method;
        method = 'get';
      }

      method = method.toLowerCase();
      if (['get', 'post', 'put', 'delete'].indexOf(method) === -1) {
        return reject(new Error(`ERR: ${method} not allowed, only allow get, post, put, delete`));
      }
      this.request[method.toLowerCase()](path, params, headers).then(resolve).catch(reject);
    });
  }

  async sendRequest({ path, method, params, headers }) {
    const language = localStorage.getItem('language') || "vi"
    if (headers) {
      headers['Accept-Language'] = language
    } else {
      headers = {
        'Accept-Language': language
      }
    }
    if (method.toLowerCase() === 'get') {
      params = { params, headers };
    }
    try {
      const res = await this.api(path, method, params, { headers });
      return res.data;
    } catch (err) {
      const { response } = err;
      const data = response
        ? {
          ...response.data,
          statusCode: response.status,
        }
        : {
          statusCode: 500,
          message: err.message,
        };

      return Promise.reject(data);
    }
  }
}

export default new Client();
